// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { showSuiErrorSnackbar } from "components/SuiSnackbar";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Images
import curved6 from "assets/images/curved-images/curved6.jpg";

import { useAuth } from "auth";
import {
  isStrongPassword,
  normalizeAuthIdentifier,
} from "auth/auth-credentials";
import { useLocale } from "lib/i18n/client";
import { resolveTooManyAttemptsMessage } from "auth/too-many-attempts";
import { getTranslations } from "lib/i18n/dictionary";
import { withLocale } from "lib/i18n/path";
import { useLocalizedNavigate } from "lib/i18n/navigation";
import { getPublicSiteOrigin } from "lib/public-site";

function Basic() {
  const navigate = useLocalizedNavigate();
  const {
    signUp,
    isAuthenticated,
    isInitializing,
    activeProfileUuid,
    profiles,
  } = useAuth();
  const locale = useLocale();
  const translations = getTranslations(locale);
  const t = translations.auth;
  const authPage = translations.authPage;
  const termsAndConditionsUrl = `${getPublicSiteOrigin()}${withLocale(
    "/terms-and-conditions",
    locale
  )}`;

  const [agreement, setAgreement] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isIdentifierTouched, setIsIdentifierTouched] = useState(false);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [postAuthRedirect, setPostAuthRedirect] = useState("dashboard");

  const normalizedIdentifier = normalizeAuthIdentifier(identifier);
  const normalizedPassword = password.trim();
  const normalizedFirstName = firstName.trim();
  const normalizedLastName = lastName.trim();
  const identifierHasValue = identifier.trim().length > 0;
  const passwordHasValue = normalizedPassword.length > 0;
  const identifierError = isIdentifierTouched && identifierHasValue && !normalizedIdentifier;
  const passwordWeak =
    isPasswordTouched && passwordHasValue && !isStrongPassword(normalizedPassword);
  const redirectProfileUuid = activeProfileUuid || profiles[0]?.uuid || "";

  useEffect(() => {
    if (isInitializing || !isAuthenticated) {
      return;
    }

    if (postAuthRedirect === "profile" && redirectProfileUuid) {
      navigate(`/profile/${redirectProfileUuid}`, { replace: true });
      return;
    }

    navigate("/dashboard", { replace: true });
  }, [isAuthenticated, isInitializing, navigate, postAuthRedirect, redirectProfileUuid]);

  useEffect(() => {
    if (errorMessage) {
      showSuiErrorSnackbar(errorMessage);
    }
  }, [errorMessage]);

  const handleSetAgreement = () => setAgreement(!agreement);
  const handleIdentifierBlur = () => {
    setIsIdentifierTouched(true);
    if (normalizedIdentifier && normalizedIdentifier !== identifier) {
      setIdentifier(normalizedIdentifier);
    }
  };
  const handlePasswordBlur = () => setIsPasswordTouched(true);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !normalizedFirstName ||
      !normalizedLastName ||
      !normalizedIdentifier ||
      !normalizedPassword
    ) {
      setErrorMessage(
        authPage.signUpMissingFields
      );
      return;
    }

    if (!isStrongPassword(normalizedPassword)) {
      setErrorMessage(t.passwordNeedsLetterAndNumber);
      return;
    }

    if (!agreement) {
      setErrorMessage(authPage.acceptTermsRequired);
      return;
    }

    setErrorMessage("");
    setIsSubmitting(true);
    setPostAuthRedirect("profile");
    if (normalizedIdentifier !== identifier) {
      setIdentifier(normalizedIdentifier);
    }

    try {
      await signUp({
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
        username: normalizedIdentifier,
        password: normalizedPassword,
      });
    } catch (error) {
      setPostAuthRedirect("dashboard");
      const tooManyAttemptsMessage = resolveTooManyAttemptsMessage(
        error,
        authPage.tooManyAttempts
      );
      if (tooManyAttemptsMessage) {
        setErrorMessage(tooManyAttemptsMessage);
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(authPage.signUpFailed);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasicLayout
      title={authPage.heroTitle}
      description={authPage.heroDescription}
      image={curved6}
    >
      <Card>
        <SoftBox p={3} mb={1} textAlign="center">
          <SoftTypography variant="h5" fontWeight="medium">
            {authPage.signUpHeading}
          </SoftTypography>
        </SoftBox>
        <SoftBox pt={2} pb={3} px={3}>
          <SoftBox component="form" role="form" onSubmit={handleSubmit}>
            <SoftBox mb={2} display="flex" gap={2}>
              <SoftInput
                placeholder={t.firstName}
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
              <SoftInput
                placeholder={t.lastName}
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput
                placeholder={t.identifier}
                autoComplete="username"
                inputProps={{
                  inputMode: identifier.includes("@") ? "email" : "tel",
                }}
                error={identifierError}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                onBlur={handleIdentifierBlur}
              />
              {identifierError && (
                <SoftTypography
                  variant="caption"
                  color="error"
                  fontWeight="medium"
                >
                  {t.identifierInvalid}
                </SoftTypography>
              )}
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput
                type="password"
                placeholder={t.password}
                autoComplete="new-password"
                error={passwordWeak}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={handlePasswordBlur}
              />
              <SoftTypography
                variant="caption"
                color={passwordWeak ? "error" : "text"}
                fontWeight={passwordWeak ? "medium" : "regular"}
              >
                {passwordWeak
                  ? t.passwordNeedsLetterAndNumber
                  : t.passwordHintSignUp}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center">
              <Checkbox checked={agreement} onChange={handleSetAgreement} />
              <SoftTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetAgreement}
                sx={{ cursor: "pointer", userSelect: "none" }}
              >
                &nbsp;&nbsp;{authPage.agreePrefix}&nbsp;
              </SoftTypography>
              <SoftTypography
                component="a"
                href={termsAndConditionsUrl}
                variant="button"
                fontWeight="bold"
                textGradient
                target="_blank"
                rel="noreferrer"
              >
                {authPage.termsAndConditions}
              </SoftTypography>
            </SoftBox>
            <SoftBox mt={4} mb={1}>
              <SoftButton
                type="submit"
                variant="gradient"
                color="dark"
                fullWidth
                disabled={
                  isSubmitting ||
                  !normalizedFirstName ||
                  !normalizedLastName ||
                  !identifier.trim() ||
                  !normalizedPassword
                }
              >
                {isSubmitting ? authPage.signUpSubmitting : authPage.signUpSubmit}
              </SoftButton>
            </SoftBox>
            <SoftBox mt={3} textAlign="center">
              <SoftTypography variant="button" color="text" fontWeight="regular">
                {authPage.alreadyHaveAccount}&nbsp;
                <SoftTypography
                  component={Link}
                  to={withLocale("/sign-in", locale)}
                  variant="button"
                  color="dark"
                  fontWeight="bold"
                  textGradient
                >
                  {t.signIn}
                </SoftTypography>
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
